import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';
import { associateService } from '../../services/associateService';
import { cepService } from '../../services/cepService';
import { planService } from '../../services/planService';
import { AssociateFormData, Plan } from '../../types';
import { maskCPF, maskPhone, maskCEP } from '../../utils/masks';
import {
  validateCPF,
  validateEmail,
  validatePhone,
  validateCEP,
  validateRequired,
  validateDate,
} from '../../utils/validators';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const STORAGE_URL = API_URL.replace('/api', '') + '/storage';

const AssociateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fotoPreview, setFotoPreview] = useState<string>('');
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const [formData, setFormData] = useState<AssociateFormData>({
    nome: '',
    cpf: '',
    data_nascimento: '',
    data_adesao: new Date().toISOString().split('T')[0],
    email: '',
    telefone: '',
    celular: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    foto: null,
    status: 'ativo',
    plan_id: undefined,
    is_hospital_employee: false,
    employee_cpf: '',
    employee_registration: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    if (isEditing) {
      loadAssociate();
    }
  }, [id]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const allPlans = await planService.getAll();
      // Filtrar apenas planos ativos
      const activePlans = allPlans.filter(plan => plan.status === 'ativo');
      setPlans(activePlans);
    } catch (err) {
      console.error('Erro ao carregar planos:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadAssociate = async () => {
    try {
      setLoading(true);
      const associate = await associateService.getById(Number(id));
      setFormData({
        nome: associate.nome || '',
        cpf: associate.cpf || '',
        data_nascimento: associate.data_nascimento || '',
        data_adesao: associate.data_adesao || new Date().toISOString().split('T')[0],
        email: associate.email || '',
        telefone: associate.telefone || '',
        celular: associate.celular || '',
        cep: associate.cep || '',
        endereco: associate.endereco || '',
        numero: associate.numero || '',
        complemento: associate.complemento || '',
        bairro: associate.bairro || '',
        cidade: associate.cidade || '',
        estado: associate.estado || '',
        foto: null,
        status: associate.status || 'ativo',
        plan_id: associate.plan_id || undefined,
        is_hospital_employee: associate.is_hospital_employee || false,
        employee_cpf: associate.employee_cpf || '',
        employee_registration: associate.employee_registration || '',
      });
      if (associate.foto) {
        setFotoPreview(`${STORAGE_URL}/${associate.foto}`);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar associado');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    let processedValue: any = value;

    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;

      // Se desmarcar "é funcionário do hospital", limpar os campos relacionados
      if (name === 'is_hospital_employee' && !processedValue) {
        setFormData((prev) => ({
          ...prev,
          [name]: processedValue,
          employee_cpf: '',
          employee_registration: '',
        }));
        return;
      }
    } else if (name === 'cpf' || name === 'employee_cpf') {
      processedValue = maskCPF(value);
    } else if (name === 'telefone' || name === 'celular') {
      processedValue = maskPhone(value);
    } else if (name === 'cep') {
      processedValue = maskCEP(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, foto: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCEPBlur = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;

    setLoadingCEP(true);
    try {
      const data = await cepService.buscarCep(cep);
      if (data) {
        setFormData((prev) => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf,
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setLoadingCEP(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.nome)) {
      newErrors.nome = 'Nome � obrigat�rio';
    }

    if (!validateRequired(formData.cpf)) {
      newErrors.cpf = 'CPF � obrigat�rio';
    } else if (!validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inv�lido';
    }

    if (!validateRequired(formData.data_nascimento)) {
      newErrors.data_nascimento = 'Data de nascimento é obrigatória';
    } else if (!validateDate(formData.data_nascimento)) {
      newErrors.data_nascimento = 'Data inválida';
    }

    if (!validateRequired(formData.data_adesao || '')) {
      newErrors.data_adesao = 'Data de adesão é obrigatória';
    } else if (!validateDate(formData.data_adesao || '')) {
      newErrors.data_adesao = 'Data inválida';
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email � obrigat�rio';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inv�lido';
    }

    if (!validateRequired(formData.telefone)) {
      newErrors.telefone = 'Telefone � obrigat�rio';
    } else if (!validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inv�lido';
    }

    if (!validateRequired(formData.cep)) {
      newErrors.cep = 'CEP � obrigat�rio';
    } else if (!validateCEP(formData.cep)) {
      newErrors.cep = 'CEP inv�lido';
    }

    if (!validateRequired(formData.endereco)) {
      newErrors.endereco = 'Endere�o � obrigat�rio';
    }

    if (!validateRequired(formData.numero)) {
      newErrors.numero = 'N�mero � obrigat�rio';
    }

    if (!validateRequired(formData.bairro)) {
      newErrors.bairro = 'Bairro � obrigat�rio';
    }

    if (!validateRequired(formData.cidade)) {
      newErrors.cidade = 'Cidade � obrigat�ria';
    }

    if (!validateRequired(formData.estado)) {
      newErrors.estado = 'Estado é obrigatório';
    }

    // Validar plano apenas na criação (não na edição)
    if (!isEditing && !formData.plan_id) {
      newErrors.plan_id = 'Plano é obrigatório';
    }

    // Validar campos de funcionário do hospital se marcado
    if (formData.is_hospital_employee) {
      if (!validateRequired(formData.employee_cpf || '')) {
        newErrors.employee_cpf = 'CPF do funcion�rio � obrigat�rio';
      } else if (!validateCPF(formData.employee_cpf || '')) {
        newErrors.employee_cpf = 'CPF do funcion�rio inv�lido';
      }

      if (!validateRequired(formData.employee_registration || '')) {
        newErrors.employee_registration = 'Matr�cula do funcion�rio � obrigat�ria';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (isEditing) {
        await associateService.update(Number(id), formData);
        setSuccess('Associado atualizado com sucesso!');
      } else {
        await associateService.create(formData);
        setSuccess('Associado criado com sucesso!');
      }

      setTimeout(() => {
        navigate('/associates');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar associado');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Associado' : 'Novo Associado'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Preencha os dados do associado
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/associates')}>
          Voltar
        </Button>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto do Associado
            </label>
            <div className="flex items-center space-x-4">
              {fotoPreview && (
                <img
                  src={fotoPreview}
                  alt="Preview"
                  className="w-24 h-24 rounded-full object-cover"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFotoChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input
                label="Nome Completo"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                error={errors.nome}
                required
              />
            </div>

            <Input
              label="CPF"
              name="cpf"
              value={formData.cpf}
              onChange={handleChange}
              error={errors.cpf}
              maxLength={14}
              required
            />

            <Input
              label="Data de Nascimento"
              name="data_nascimento"
              type="date"
              value={formData.data_nascimento}
              onChange={handleChange}
              error={errors.data_nascimento}
              required
            />

            <Input
              label="Data de Adesão"
              name="data_adesao"
              type="date"
              value={formData.data_adesao}
              onChange={handleChange}
              error={errors.data_adesao}
              required
            />

            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
            />

            <Input
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              as="select"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="suspenso">Suspenso</option>
            </Input>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plano {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <select
                name="plan_id"
                value={formData.plan_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    plan_id: value ? Number(value) : undefined,
                  }));
                  if (errors.plan_id) {
                    setErrors((prev) => ({ ...prev, plan_id: '' }));
                  }
                }}
                disabled={loadingPlans}
                className={`block w-full rounded-lg border ${
                  errors.plan_id ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100`}
              >
                <option value="">
                  {loadingPlans ? 'Carregando planos...' : 'Selecione um plano'}
                </option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.nome} - {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(plan.valor)}
                  </option>
                ))}
              </select>
              {errors.plan_id && (
                <p className="mt-1 text-sm text-red-500">{errors.plan_id}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Vínculo com Hospital
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_hospital_employee"
                  name="is_hospital_employee"
                  checked={formData.is_hospital_employee || false}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_hospital_employee"
                  className="ml-2 block text-sm text-gray-900"
                >
                  É funcionário do hospital?
                </label>
              </div>

              {formData.is_hospital_employee && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 pl-6 pt-2">
                  <Input
                    label="CPF do Funcionário"
                    name="employee_cpf"
                    value={formData.employee_cpf || ''}
                    onChange={handleChange}
                    error={errors.employee_cpf}
                    maxLength={14}
                    required
                    placeholder="000.000.000-00"
                  />

                  <Input
                    label="Matrícula"
                    name="employee_registration"
                    value={formData.employee_registration || ''}
                    onChange={handleChange}
                    error={errors.employee_registration}
                    required
                    placeholder="Número da matrícula"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contato</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Input
                label="Telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                error={errors.telefone}
                maxLength={15}
                required
              />

              <Input
                label="Celular"
                name="celular"
                value={formData.celular}
                onChange={handleChange}
                maxLength={15}
              />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Endere�o</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Input
                label="CEP"
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                onBlur={handleCEPBlur}
                error={errors.cep}
                maxLength={9}
                required
                disabled={loadingCEP}
              />

              <Input
                label="Endere�o"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                error={errors.endereco}
                required
                disabled={loadingCEP}
              />

              <Input
                label="N�mero"
                name="numero"
                value={formData.numero}
                onChange={handleChange}
                error={errors.numero}
                required
              />

              <Input
                label="Complemento"
                name="complemento"
                value={formData.complemento}
                onChange={handleChange}
              />

              <Input
                label="Bairro"
                name="bairro"
                value={formData.bairro}
                onChange={handleChange}
                error={errors.bairro}
                required
                disabled={loadingCEP}
              />

              <Input
                label="Cidade"
                name="cidade"
                value={formData.cidade}
                onChange={handleChange}
                error={errors.cidade}
                required
                disabled={loadingCEP}
              />

              <Input
                label="Estado"
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                error={errors.estado}
                maxLength={2}
                required
                disabled={loadingCEP}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/associates')}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {isEditing ? 'Atualizar' : 'Criar'} Associado
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AssociateForm;
